/**
 * The onboarding flow rules, as pure functions.
 *
 * This file exists because of a bug class the custom-widget work has already
 * been bitten by once: the mock host and the real host disagreeing, so a widget
 * passed its tests and failed in production. Onboarding is the worst possible
 * place for that — the failure mode is a customer stuck mid-signup — so the step
 * ordering, the expiry arithmetic and the prerequisite gate live here, with no
 * platform and no framework in sight, and **both** hosts import them:
 * `mock-host.ts` and the web-frontend bridge
 * (`custom-widget-onboarding.ts`). Neither reimplements a rule.
 *
 * What is *not* here: anything requiring a backend (sending a code, checking it,
 * submitting). Those differ by definition; only the rules around them are shared.
 */

import {
  AppOnboardingCheckStatus,
  AppOnboardingCredential,
  AppOnboardingFlow,
  AppOnboardingKind,
  AppOnboardingRequirements,
  AppOnboardingStep,
  AppOnboardingStepId,
  AppVerificationChannel,
} from './contracts';

/**
 * How long a contact verification lasts when the project does not configure it.
 * Mirrors the platform's own server-side default — the cache entry the submit
 * gate reads has a 30-minute TTL unless a project overrides it.
 *
 * A host that cannot read the project's TTL MUST use this rather than treating
 * verification as eternal: the server expires it either way, and the only
 * difference is whether the customer finds out from the widget or from a 403.
 */
export const DEFAULT_VERIFIED_CONTACT_TTL_MINUTES = 30;

/**
 * Digits in the OTP, per channel. Not a preference — the platform generates a
 * 4-digit email code and a 6-digit phone code, and a code-entry UI that renders
 * the wrong number of boxes is unusable.
 */
export const OTP_CODE_LENGTH: Record<AppVerificationChannel, number> = {
  email: 4,
  phone: 6,
};

/**
 * How long a resend button should stay disabled. Matches the native OTP widget's
 * countdown, and sits inside the platform's 3-per-minute limit — three sends at
 * this spacing cannot trip it.
 */
export const OTP_RESEND_AFTER_SECONDS = 60;

/**
 * The platform's rate limit on the OTP endpoints: 3 requests per 60 seconds, per
 * IP, per channel. Hosts surface a breach as 'rate-limited'; the mock host
 * enforces the same numbers so a resend loop that survives locally survives in
 * production.
 */
export const OTP_RATE_LIMIT = { maxRequests: 3, windowSeconds: 60 };

/**
 * Fields every individual application needs, whatever the provider. These are
 * exactly the typed members of OnboardIndividualRequest that are not optional —
 * the list requirements() reports, so a widget can render a checklist without
 * duplicating the type.
 */
export const ONBOARDING_INDIVIDUAL_REQUIRED_FIELDS = [
  'firstName',
  'lastName',
  'dob',
  'email',
  'phone',
  'address.country',
  'address.addressLine1',
  'address.city',
  'address.postalCode',
] as const;

/**
 * Fields every company application needs. `members` is required here even though
 * the wire DTO marks it optional, because the service rejects an empty list —
 * see OnboardBusinessRequest.
 */
export const ONBOARDING_BUSINESS_REQUIRED_FIELDS = [
  'name',
  'identificationNumber',
  'companyAddress.country',
  'companyAddress.city',
  'companyAddress.postalCode',
  'members',
  'members[].position',
  'members[].firstName',
  'members[].lastName',
  'members[].email',
  'members[].phone',
  'members[].dob',
] as const;

/** Where a host is up to on one contact channel. */
export interface OnboardingVerificationState {
  /** The value most recently verified on this channel; absent if none was. */
  value?: string;
  /** ISO timestamp that verification lapses at. */
  expiresAt?: string;
}

export type OnboardingVerificationStates = Partial<
  Record<AppVerificationChannel, OnboardingVerificationState>
>;

/** Everything the rules need to answer flow()/the gate. Hosts assemble it. */
export interface OnboardingFlowInput {
  kind: AppOnboardingKind;
  /**
   * The contact values the widget intends to submit. When absent, a live
   * verification of *any* value counts — which is what a widget that has not yet
   * collected the details wants to see, and is why submit() never relies on it.
   */
  email?: string;
  phone?: string;
  verified: OnboardingVerificationStates;
  /** Whether an application has already been submitted for this applicant. */
  submitted: boolean;
  /** Signed-in code applications can run Veriff before collecting the full profile. */
  identityBeforeSubmit?: boolean;
  /** Normalised KYC/KYB status, used only to mark the identity step done. */
  identityStatus?: AppOnboardingCheckStatus;
  /** Inlined onto the 'details' step when the host knows it. */
  requirements?: AppOnboardingRequirements;
  /** Injectable clock, for deterministic tests. Defaults to Date.now(). */
  now?: number;
}

/** The two channels, in the order a stepper should show them. */
export const VERIFICATION_CHANNELS: readonly AppVerificationChannel[] = ['email', 'phone'];

const VERIFY_STEP: Record<AppVerificationChannel, AppOnboardingStepId> = {
  email: 'verify.email',
  phone: 'verify.phone',
};

/**
 * The ISO expiry of a verification that happened at `verifiedAt`, given the
 * project's TTL. An absent or unparseable TTL falls back to the platform default
 * rather than to "never" — see DEFAULT_VERIFIED_CONTACT_TTL_MINUTES.
 */
export function verificationExpiresAt(
  verifiedAt: Date | string | number,
  ttlMinutes?: number | string | null,
): string {
  const verifiedMs = new Date(verifiedAt).getTime();
  const base = Number.isNaN(verifiedMs) ? Date.now() : verifiedMs;
  const parsed = Number(ttlMinutes);
  const minutes = Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_VERIFIED_CONTACT_TTL_MINUTES;
  return new Date(base + minutes * 60_000).toISOString();
}

/**
 * Whether a channel is verified *for the value that matters*, and not expired.
 *
 * The value comparison is the part that is easy to leave out and expensive to
 * leave out: the server's gate is keyed by the contact string, so a widget that
 * verified one address and submits another gets a 403 no matter how recently it
 * verified. An absent `value` argument means "any live verification will do",
 * which is only ever right for rendering a stepper — never for a submit.
 */
export function isVerificationLive(
  state: OnboardingVerificationState | undefined,
  value?: string,
  now: number = Date.now(),
): boolean {
  if (!state?.expiresAt) return false;
  const expiresMs = new Date(state.expiresAt).getTime();
  if (Number.isNaN(expiresMs) || expiresMs <= now) return false;
  if (value === undefined) return true;
  return normaliseContactValue(state.value) === normaliseContactValue(value);
}

/**
 * Contact values are compared case- and whitespace-insensitively, because that
 * is the only difference a customer can introduce between typing an address into
 * a verify field and typing it into a details field. Nothing else is normalised:
 * a phone number missing its country code is a *different* number to the
 * platform, and pretending otherwise would produce a green tick and a 403.
 */
export function normaliseContactValue(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * The steps still outstanding before a submit may be attempted, in flow order.
 * Empty means the gate is satisfied.
 *
 * Both channels are always required — that is the platform's rule for an
 * individual, and the host applies it to a business too (of the primary member),
 * because a widget flow should not change shape with the entity type.
 */
export function outstandingPrerequisites(
  input: Pick<OnboardingFlowInput, 'verified' | 'email' | 'phone' | 'now'>,
): AppOnboardingStepId[] {
  const now = input.now ?? Date.now();
  const outstanding: AppOnboardingStepId[] = [];
  for (const channel of VERIFICATION_CHANNELS) {
    const value = channel === 'email' ? input.email : input.phone;
    if (!isVerificationLive(input.verified[channel], value, now)) {
      outstanding.push(VERIFY_STEP[channel]);
    }
  }
  return outstanding;
}

/**
 * The flow a widget renders its stepper from. Deliberately the *only* place the
 * step order is written down, so no widget and no host has its own copy.
 */
export function buildOnboardingFlow(input: OnboardingFlowInput): AppOnboardingFlow {
  const now = input.now ?? Date.now();
  const steps: AppOnboardingStep[] = [];

  for (const channel of VERIFICATION_CHANNELS) {
    const value = channel === 'email' ? input.email : input.phone;
    const state = input.verified[channel];
    const live = isVerificationLive(state, value, now);
    const step: AppOnboardingStep = {
      id: VERIFY_STEP[channel],
      status: live ? 'done' : 'available',
    };
    // Only meaningful on a live verification: an expiry in the past is not a
    // warning a widget should render, it is an unverified channel.
    if (live && state?.expiresAt) step.expiresAt = state.expiresAt;
    steps.push(step);
  }

  const details: AppOnboardingStep = { id: 'details', status: 'available' };
  if (input.requirements) {
    details.requiredFields = input.requirements.requiredFields;
    details.extraFields = input.requirements.extraFields;
    details.extraFieldsKnown = input.requirements.extraFieldsKnown;
  }
  steps.push(details);

  const outstanding = outstandingPrerequisites(input);
  const submit: AppOnboardingStep = input.submitted
    ? { id: 'submit', status: 'done' }
    : outstanding.length
      ? { id: 'submit', status: 'blocked', blockedBy: outstanding }
      : { id: 'submit', status: 'available' };
  steps.push(submit);

  const identity: AppOnboardingStep =
    input.identityStatus === 'success'
      ? { id: 'identity', status: 'done' }
      : input.submitted || input.identityBeforeSubmit
        ? { id: 'identity', status: 'available' }
        : { id: 'identity', status: 'blocked', blockedBy: ['submit'] };
  steps.push(identity);

  return {
    kind: input.kind,
    // Already submitted is not "can submit again": a second application is a
    // duplicate, and the platform answers it with a wedged 409.
    canSubmit: submit.status === 'available',
    steps,
  };
}

/**
 * Collapses the platform's KYC and KYB vocabularies into the one a widget sees.
 *
 * The platform already normalises across providers; this only removes the
 * duplicates ('kyc-pending', 'kyb-pending' and 'review-pending' all mean
 * "awaiting a decision") and maps 'started' onto 'in-progress'.
 *
 * Anything unrecognised — including a value a future provider introduces —
 * becomes 'pending', which is the only safe default: a widget must never read
 * drift as success or as a final failure, and 'pending' is the answer that keeps
 * it waiting. An empty/absent status is 'not-started', which is what the platform
 * means by it.
 */
export function normaliseOnboardingCheckStatus(raw: unknown): AppOnboardingCheckStatus {
  const value = String(raw ?? '')
    .trim()
    .toLowerCase();
  switch (value) {
    case '':
    case 'not-started':
      return 'not-started';
    case 'started':
    case 'in-progress':
      return 'in-progress';
    case 'success':
      return 'success';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    case 'pending':
    case 'kyc-pending':
    case 'kyb-pending':
    case 'review-pending':
      return 'pending';
    default:
      return 'pending';
  }
}

/**
 * Whether a status means the host can stop polling. 'pending' and 'in-progress'
 * are not terminal — and neither is anything unrecognised, which is why
 * normaliseOnboardingCheckStatus maps drift to 'pending'.
 */
export function isTerminalCheckStatus(status: AppOnboardingCheckStatus): boolean {
  return status === 'success' || status === 'failed' || status === 'cancelled';
}

/**
 * The message a rejected OTP produces. Shared so the mock host and the real host
 * are indistinguishable to a widget that shows the reason to the customer —
 * including in a snapshot test.
 */
export const OTP_CODE_REJECTED_MESSAGE =
  'onboarding.verify.confirm: the code was not accepted (wrong or expired)';

/** The message of a prerequisite-gate rejection. Shared for the same reason. */
export function prerequisiteUnmetMessage(
  operation: string,
  outstanding: AppOnboardingStepId[],
): string {
  return `${operation} requires these onboarding steps first: ${outstanding.join(', ')}`;
}

/** The required-field list for a kind. */
export function onboardingRequiredFields(kind: AppOnboardingKind): string[] {
  return [
    ...(kind === 'business'
      ? ONBOARDING_BUSINESS_REQUIRED_FIELDS
      : ONBOARDING_INDIVIDUAL_REQUIRED_FIELDS),
  ];
}

/** Small public policy packet only; failures and legacy/malformed responses never enable password collection. */
export function normaliseOnboardingCredential(value: unknown): AppOnboardingCredential {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { kind: 'unavailable' };
  const packet = value as Record<string, unknown>;
  if (!Object.hasOwn(packet, 'kind')) return { kind: 'unavailable' };
  if (Object.keys(packet).length === 1 && packet['kind'] === 'password') return { kind: 'password' };
  if (Object.keys(packet).length === 3 && packet['kind'] === 'pin' &&
    Object.hasOwn(packet, 'minLength') && Object.hasOwn(packet, 'maxLength') &&
    Number.isInteger(packet['minLength']) && Number.isInteger(packet['maxLength']) &&
    Number(packet['minLength']) >= 1 && Number(packet['maxLength']) <= 64 && Number(packet['minLength']) <= Number(packet['maxLength'])) {
    return { kind: 'pin', minLength: Number(packet['minLength']), maxLength: Number(packet['maxLength']) };
  }
  return { kind: 'unavailable' };
}
